import { React, _ } from '../../lib';
import { ContactWidgetHook } from '../component-address-contact';
import { validateTime } from '../component-main';
import { Checkbox as CheckboxInner } from './checkbox';
import { ColorPicker } from './color';
import { Combobox as ComboboxInner } from './combobox';
import { DateBox } from './date';
import { TextSearched } from './text';
import { Textbox } from './textbox';
const Inf = Infinity;
export const checkReadOnly = (readOnly, record) => {
    if (readOnly == null) {
        return false;
    }
    if (_.isBoolean(readOnly)) {
        return readOnly;
    }
    return readOnly(record);
};
export var GridCell;
(function (GridCell) {
    GridCell.StaticString = (field) => ({
        ..._.omit(field, 'data'),
        onUpdate: () => { },
        readOnly: true,
        hideIndicators: true,
        cell: {
            text: x => x,
            sortVal: x => x.toLowerCase(),
            className: () => 'static-text string',
            readOnly: true,
            cmpt: ({ value, searchText }) => (React.createElement("span", { title: String(value ?? '') },
                React.createElement(TextSearched, { text: String(value ?? ''), needles: searchText ? searchText.split(/\s+/g) : null }))),
            validation: [],
        },
    });
    GridCell.StaticNumber = (field) => ({
        ..._.omit(field, 'data'),
        onUpdate: () => { },
        readOnly: true,
        sortVal: field.sortVal ?? field.value,
        hideIndicators: true,
        cell: {
            text: x => (x ? String(x) : ''),
            sortVal: x => x,
            className: () => 'static-text number',
            readOnly: true,
            cmpt: ({ value, searchText }) => (React.createElement("span", { title: String(value ?? '') },
                React.createElement(TextSearched, { text: String(value ?? ''), needles: searchText ? searchText.split(/\s+/g) : null }))),
            validation: [],
        },
    });
    GridCell.StaticJSX = (field) => ({
        ..._.omit(field, 'data'),
        value: () => undefined,
        onUpdate: () => { },
        readOnly: true,
        sortVal: field.sortVal,
        validation: [],
        cell: {
            readOnly: true,
            validation: [],
            cmpt: x => field.cell({
                record: x.record,
                ref: x.ref,
                searchText: x.searchText,
                onFocus: x.onFocus,
                onBlur: x.onBlur,
            }),
        },
    });
    GridCell.Checkbox = (field) => ({
        ..._.omit(field, 'data'),
        cell: {
            text: x => (x ? 'Yes' : 'No'),
            sortVal: x => (x ? 1 : 0),
            className: () => 'checkbox',
            proxyValue: x => x,
            readOnly: typeof field?.readOnly === 'boolean' ? field?.readOnly : false,
            cmpt: ({ value, ref, readOnly, onUpdate, onFocus, onBlur }) => (React.createElement(CheckboxInner, { ref: ref, value: value, onUpdate: v => {
                    onUpdate(v, true);
                }, onFocus: onFocus, onBlur: onBlur, readOnly: readOnly, lbl: "" })),
            validation: [],
        },
    });
    GridCell.Text = (field) => ({
        ..._.omit(field, 'data'),
        cell: {
            text: x => x,
            sortVal: field?.sortVal ?? (x => String(x ?? '').toLowerCase()),
            className: () => 'textbox',
            proxyValue: field.cell?.proxyValue ?? (x => x),
            readOnly: field?.readOnly,
            cmpt: ({ value, ref, readOnly, onUpdate, onFocus, onBlur, onCommit, searchText, }) => (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "search-shadow" },
                    React.createElement(TextSearched, { text: String(value ?? ''), needles: searchText ? searchText.split(/\s+/g) : null })),
                React.createElement(Textbox, { ref: ref, value: value ?? '', onUpdate: v => {
                        onUpdate(v);
                    }, onFocus: onFocus, onKeyDown: e => {
                        if (e.key == 'Enter') {
                            e.preventDefault();
                            onCommit();
                        }
                    }, onBlur: () => {
                        let v = value;
                        if (field.cell?.fixOnBlur) {
                            v = field.cell?.fixOnBlur(v);
                        }
                        if (field.cell?.trim) {
                            v = (v?.trim() ?? null);
                        }
                        onUpdate(v, true);
                        onBlur();
                    }, placeholder: field.cell?.placeholder, readOnly: readOnly }))),
            validation: [
                {
                    req: (x) => (x?.length ?? 0) >= (field.cell?.minLength ?? 0),
                    msg: `min length ${field.cell?.minLength ?? 0}`,
                },
                {
                    req: (x) => (x?.length ?? 0) <= (field.cell?.maxLength ?? Inf),
                    msg: `max length ${field.cell?.maxLength ?? Inf}`,
                },
                ...(field.cell?.validations ?? []),
            ],
        },
    });
    GridCell.Numeric = (field) => {
        const fixOnBlur = (v) => {
            if (!v || v === '') {
                return null;
            }
            const nv = isNaN(+v) || v === '' ? '' : (+v).toFixed(field.cell?.decimals);
            if (field.cell?.fixOnBlur) {
                return field.cell.fixOnBlur(nv);
            }
            return isNaN(+nv) ? '' : +nv;
        };
        return {
            ..._.omit(field, 'data'),
            cell: {
                text: x => String(x ?? ''),
                sortVal: x => Number(x || 0),
                className: () => 'textbox',
                proxyValue: v => {
                    const nv = String(v).replace(/[^0-9\.]/g, '');
                    if (field.cell?.proxyValue) {
                        return field.cell?.proxyValue(nv);
                    }
                    if (nv == '') {
                        return null;
                    }
                    return nv;
                },
                readOnly: field?.readOnly,
                cmpt: ({ value, ref, readOnly, onUpdate, onFocus, onCommit, searchText, }) => (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "search-shadow" },
                        React.createElement(TextSearched, { text: String(value ?? ''), needles: searchText ? searchText.split(/\s+/g) : null })),
                    React.createElement(Textbox, { ref: ref, value: String(value ?? ''), onUpdate: v => {
                            onUpdate(String(v));
                        }, onFocus: onFocus, onKeyDown: e => {
                            if (e.key == 'Enter') {
                                e.preventDefault();
                                onCommit();
                            }
                        }, style: { textAlign: 'right' }, onBlur: () => {
                            let v = value;
                            if (fixOnBlur) {
                                v = fixOnBlur(v ?? '');
                            }
                            if (v === '') {
                                v = null;
                            }
                            if (v && !isNaN(+v)) {
                                v = (+v).toFixed(field.cell?.decimals ?? 0);
                            }
                            onUpdate(v, true);
                        }, placeholder: field.cell?.placeholder, readOnly: readOnly }))),
                validation: [
                    {
                        req: (x) => (x?.length ?? 0) >= (field.cell?.minLength ?? 0),
                        msg: `min length ${field.cell?.minLength ?? 0}`,
                    },
                    {
                        req: (x) => (x?.length ?? 0) <= (field.cell?.maxLength ?? Inf),
                        msg: `max length ${field.cell?.maxLength ?? Inf}`,
                    },
                    ...(field.cell?.validations ?? []),
                ],
            },
        };
    };
    GridCell.Combobox = (field) => {
        const getTextWrapped = (x) => {
            if (x == null) {
                return field.cell?.nullable ?? '';
            }
            return field.cell?.getText(x);
        };
        return {
            ..._.omit(field, 'data'),
            cell: {
                text: x => getTextWrapped(x),
                sortVal: x => getTextWrapped(x).toLowerCase(),
                className: () => 'combobox',
                cmpt: ({ record, value, ref, readOnly, onUpdate, onFocus, onBlur, searchText, }) => (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "search-shadow" },
                        React.createElement(TextSearched, { text: String(getTextWrapped(value)), needles: searchText ? searchText.split(/\s+/g) : null })),
                    React.createElement(ComboboxInner, { disabled: readOnly, ref: ref, value: value, onUpdate: v => {
                            onUpdate(v, true);
                        }, nullable: field.cell?.nullable, onFocus: onFocus, onBlur: onBlur, placeholder: field.cell?.placeholder, options: _.isFunction(field.cell?.options)
                            ? field.cell?.options(record)
                            : field.cell?.options }))),
                validation: [],
            },
        };
    };
    GridCell.ComboboxMulti = (field) => {
        const getText = (value) => value.map(x => field.cell?.getText(x)).join(', ');
        return {
            ..._.omit(field, 'data'),
            cell: {
                text: x => getText(x),
                sortVal: x => getText(x).toLowerCase(),
                className: () => 'combobox',
                cmpt: ({ value, ref, onUpdate, onFocus, onBlur, searchText }) => (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "search-shadow" },
                        React.createElement(TextSearched, { text: String(getText(value)), needles: searchText ? searchText.split(/\s+/g) : null })),
                    React.createElement(ComboboxInner, { ref: ref, value: value, onUpdate: v => {
                            onUpdate(v, true);
                        }, onFocus: onFocus, onBlur: onBlur, nullable: field.cell?.nullable, multiple: true, placeholder: field.cell?.placeholder, options: field.cell?.options }))),
                validation: [],
            },
        };
    };
    GridCell.Date = (field) => ({
        ..._.omit(field, 'data'),
        cell: {
            text: x => x?.fmt(field.cell?.format ?? 'dd/MM/yyyy') ?? '',
            sortVal: field.cell?.sortVal ?? (x => String(x ?? '').toLowerCase()),
            className: () => 'datebox',
            proxyValue: field.cell?.proxyValue ?? (x => x),
            readOnly: field?.readOnly,
            cmpt: ({ value, ref, readOnly, onUpdate, onFocus, onBlur, onCommit, searchText, }) => (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "search-shadow" },
                    React.createElement(TextSearched, { text: value?.fmt(field.cell?.format ?? 'dd/MM/yyyy') ?? '', needles: searchText ? searchText.split(/\s+/g) : null })),
                React.createElement(DateBox, { ref: ref, value: value ?? '', onUpdate: v => {
                        onUpdate(v, true);
                    }, fmt: field.cell?.format ?? 'dd/MM/yyyy', onFocus: onFocus, onKeyDown: e => {
                        if (e.key == 'Enter') {
                            e.preventDefault();
                            onCommit();
                        }
                    }, onBlur: () => {
                        let v = value;
                        if (field.cell?.fixOnBlur) {
                            v = field.cell?.fixOnBlur(v ?? null);
                        }
                        onUpdate(v, true);
                        onBlur();
                    }, placeholder: field.cell?.placeholder, readOnly: readOnly }))),
            validation: field.cell?.validations ?? [],
        },
    });
    GridCell.TextTime = (field) => GridCell.Text({
        ..._.omit(field, 'data'),
        cell: {
            minLength: 0,
            maxLength: 5,
            placeholder: field.cell?.placeholder,
            proxyValue: field.cell?.proxyValue ?? (x => x),
            fixOnBlur: v => {
                if (!v) {
                    return null;
                }
                return field.cell?.fixOnBlur
                    ? field.cell?.fixOnBlur(v)
                    : validateTime(v) || null;
            },
            sortVal: field.cell?.sortVal ?? (x => validateTime(x)),
            validations: _.compact([
                {
                    req: (x) => !x || validateTime(x) != '',
                    msg: 'Invalid time format',
                },
                !(field.cell?.nullable ?? false)
                    ? {
                        req: (x) => Boolean(x && x != ''),
                        msg: 'Time cannot be empty',
                    }
                    : undefined,
            ]),
        },
    });
    GridCell.Color = (field) => ({
        ..._.omit(field, 'data'),
        cell: {
            className: () => 'color-picker',
            cmpt: ({ value, ref, readOnly, onUpdate, onFocus, onBlur }) => (React.createElement(ColorPicker, { ref: ref, value: value ?? '', onUpdate: v => {
                    onUpdate(v, true);
                }, readOnly: readOnly, onFocus: onFocus, onBlur: onBlur, colors: field.cell?.colors })),
            validation: [],
        },
    });
    GridCell.Contact = (field) => ({
        ..._.omit(field, 'data'),
        sortable: false,
        cell: {
            text: x => x
                ? _.compact([x.Email, x.Fax, x.Mobile, x.Phone, x.Website])
                    .map(String)
                    .join(', ')
                : '',
            className: () => 'contact-widget',
            readOnly: field?.readOnly,
            cmpt: ({ value, ref, readOnly, onUpdate, onFocus, onBlur }) => (React.createElement(ContactWidgetHook, { ref: ref, value: value ?? {}, onUpdate: (v, dmodelContacts) => {
                    field.cell.updateDataModel(dmodelContacts);
                    onUpdate(v, true);
                }, onFocus: onFocus, onBlur: onBlur, readOnly: readOnly })),
            validation: field?.validation ?? [],
        },
    });
})(GridCell || (GridCell = {}));
//# sourceMappingURL=editable-cells.js.map